import { DateTime } from 'luxon';
import { client } from '@/composables/sanity/client';
import { computed, ref } from 'vue';

export const RELEASE_NOTES_STATE = { idle: 'idle', loading: 'loading', loaded: 'loaded', failed: 'failed' };

const releaseNotes = ref([]);
const releaseNotesState = ref(RELEASE_NOTES_STATE.idle);
let inFlightFetch = null;

const websiteReleaseDate = DateTime.fromISO('2024-08-07');
export default function useReleaseNotes(personalAppSettingsSvc, loggedInUser) {
  const RELEASENOTES_QUERY = (page, amountOfItems) => `
  *[_type == "releaseNote"]{
    _id,
    title,
    releaseDate,
    "tags": coalesce(tags, []),
    sections[]{
      _key,
      title,
      items[]{
        _key,
        category,
        content
      }
    },
    content[]{
      ...,
      _type == "imageEmbed" => {
          ...,
          alt,
          imageFile {
            "width": asset->metadata.dimensions.width,
            "height": asset->metadata.dimensions.height,
            "url": asset->url,
          }
      },
      _type == "videoEmbed" => {
          ...,
          "videoSrc": videoFile.asset->url
      },
    }
  } | order(dateTime(releaseDate) desc) [${(page - 1) * amountOfItems}...${page * amountOfItems}]
`;

  const fetchReleaseNotes = () => {
    if (inFlightFetch !== null) {
      return inFlightFetch;
    }
    releaseNotesState.value = RELEASE_NOTES_STATE.loading;
    inFlightFetch = client.fetch(RELEASENOTES_QUERY(1, 5))
      .then((notes) => {
        releaseNotes.value = notes;
        releaseNotesState.value = RELEASE_NOTES_STATE.loaded;
        return notes;
      })
      .catch((error) => {
        releaseNotesState.value = RELEASE_NOTES_STATE.failed;
        throw error;
      })
      .finally(() => {
        inFlightFetch = null;
      });
    return inFlightFetch;
  };

  const unreadReleaseNotes = computed(() => {
    if (!Array.isArray(personalAppSettingsSvc.personalAppSettings.value.readReleaseNotes)) {
      return [];
    }

    const firstLogin = loggedInUser.value.personalInfosCompleted ?? loggedInUser.value.activatedAt;
    if (firstLogin === null || firstLogin === undefined) {
      return [];
    }

    const firstLoginUser = DateTime.fromISO(firstLogin);

    return releaseNotes.value.filter((d) => {
      const date = DateTime.fromISO(d.releaseDate);

      // We don't show any release notes as unread from before the website release
      if (date.diff(websiteReleaseDate) < 0) {
        return false;
      }

      if (date.diff(firstLoginUser) < 0) {
        return false;
      }

      return !personalAppSettingsSvc.personalAppSettings.value.readReleaseNotes.includes(d._id);
    });
  });

  const markAsRead = () => personalAppSettingsSvc.updateSingle(
    {
      uid: personalAppSettingsSvc.personalAppSettings.value.uid,
      readReleaseNotes: [...new Set([
        ...(personalAppSettingsSvc.personalAppSettings.value.readReleaseNotes ?? []),
        ...releaseNotes.value.map((n) => n._id),
      ])],
    },
  );

  const latestReleaseNote = computed(() => releaseNotes.value.reduce(
    (latest, next) => {
      if (latest === null) {
        return next;
      }
      return DateTime.fromISO(next.releaseDate) > DateTime.fromISO(latest.releaseDate) ? next : latest;
    },
    null,
  ));

  return { unreadReleaseNotes, latestReleaseNote, markAsRead, fetchReleaseNotes, releaseNotes, releaseNotesState };
}
